import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle } from "lucide-react";

import { SectionHeading } from "../components/SectionHeading";
import { CtaBand } from "../components/CtaBand";
import { getKostenContent, type KostenContent } from "../lib/kosten.functions";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";

const title = "Kosten per dienst | Fedor Timmerwerken";
const description =
  "Wat kost een dakkapel, aanbouw, kozijn of renovatie? Bekijk per dienst waar de prijs van afhangt en vraag een vrijblijvende offerte op maat aan.";
const socialTitle = "Kosten per dienst | Fedor Timmerwerken";
const socialDescription =
  "Per dienst uitgelegd waar de prijs van afhangt, plus een vrijblijvende offerte op maat.";

const buildFaqJsonLd = (faq: KostenContent["faq"]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((item) => ({
    "@type": "Question",
    name: item.vraag,
    acceptedAnswer: { "@type": "Answer", text: item.antwoord },
  })),
});



const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: "Kosten", item: `${SITE_URL}/kosten` },
  ],
};

export const Route = createFileRoute("/kosten")({
  loader: () => getKostenContent(),
  head: ({ loaderData }) => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: socialTitle },
      { name: "twitter:title", content: socialTitle },
      { property: "og:description", content: socialDescription },
      { name: "twitter:description", content: socialDescription },
      { property: "og:url", content: `${SITE_URL}/kosten` },
      ...ogImageMeta(ogImageDefault, "Fedor Timmerwerken aan het werk in Dordrecht"),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/kosten` }],
    scripts: [
      ...(loaderData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(buildFaqJsonLd(loaderData.faq)),
            },
          ]
        : []),
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
    ],
  }),
  component: KostenPage,
});

function KostenPage() {
  const {
    items: kostenItems,
    faq: kostenFaq,
    disclaimer: kostenDisclaimer,
  }: KostenContent = Route.useLoaderData();


  return (
    <>

      <section className="border-b border-border/60 bg-secondary/20 py-14 sm:py-20">
        <div className="container-tight">
          <div className="max-w-3xl">
            <span className="font-heading text-sm font-semibold uppercase tracking-wider text-primary">
              Kosten
            </span>
            <h1 className="mt-2 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Wat kost jouw project?
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Omdat we alles ter plekke op maat maken, hangt de prijs af van je woning en je wensen.
              Hieronder vind je per dienst een richtprijs en de keuzes die de kosten bepalen. Voor
              een aanbouw hebben we een uitgebreide gids met richtprijzen per m².
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Vraag een offerte op maat aan
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/aanbouw-kosten"
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md border border-input bg-background px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
              >
                Gids: aanbouw kosten per m²
              </Link>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold">
              <Link
                to="/uitbouw-kosten-per-m2"
                className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
              >
                Uitbouw kosten per m²
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/casco-aanbouw-kosten"
                className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
              >
                Casco aanbouw kosten
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/aanbouw-2-verdiepingen-kosten"
                className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
              >
                Aanbouw over 2 verdiepingen
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                to="/kozijnen-dordrecht"
                className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
              >
                Kozijnen Dordrecht
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="container-tight">
          <SectionHeading
            eyebrow="Per dienst"
            title="Waar hangt de prijs van af?"
            description="Per dienst een richtprijs plus de keuzes die de kosten bepalen. Tijdens het gratis adviesgesprek aan huis maken we het bedrag concreet."
            align="center"
          />

          {/* Snel-scanbaar prijsoverzicht — alleen op mobiel, met sprong naar de kaart. */}
          <div className="mt-8 overflow-hidden rounded-2xl border border-border/60 bg-card sm:hidden">
            <h3 className="border-b border-border/60 bg-secondary/30 px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Richtprijzen in één oogopslag
            </h3>
            <ul className="divide-y divide-border/60">
              {kostenItems.map((item) => (
                <li key={item.slug}>
                  <a
                    href={`#kosten-${item.slug}`}
                    className="grid min-h-12 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-sm"
                  >
                    <span className="min-w-0 truncate font-medium text-foreground">
                      {item.title}
                    </span>
                    <span className="shrink-0 font-heading text-sm font-bold text-primary">
                      {item.prijs ?? "Op aanvraag"}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 grid gap-4 sm:mt-14 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {kostenItems.map((item) => (
              <article
                key={item.slug}
                id={`kosten-${item.slug}`}
                className="flex scroll-mt-24 flex-col overflow-hidden rounded-2xl border border-border/60 bg-card"
              >
                <div className="border-b border-border/60 bg-secondary/30 p-5 sm:p-6">
                  <h2 className="font-heading text-base font-semibold text-foreground sm:text-lg">
                    {item.title}
                  </h2>
                  <p className="mt-2 font-heading text-xl font-bold leading-tight tracking-tight text-primary sm:text-2xl">
                    {item.prijs ?? "Prijs op aanvraag"}
                  </p>
                  {item.prijsNoot && (
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                      {item.prijsNoot}
                    </p>
                  )}
                </div>

                <div className="flex flex-1 flex-col p-5 sm:p-6">
                  <h3 className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Prijs hangt af van
                  </h3>
                  <ul className="mt-3 flex-1 space-y-2.5 text-sm leading-relaxed text-foreground/80">
                    {item.factoren.map((factor) => (
                      <li key={factor} className="flex items-start gap-2.5">
                        <CheckCircle
                          className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        <span className="min-w-0">{factor}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 flex flex-col gap-1 border-t border-border/60 pt-2 text-sm font-semibold sm:mt-6">
                    <Link
                      to="/diensten/$slug"
                      params={{ slug: item.slug }}
                      className="inline-flex min-h-11 items-center gap-1 text-primary hover:text-primary/80 sm:min-h-0 sm:py-1"
                    >
                      Meer over {item.title.toLowerCase()}
                      <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                    </Link>
                    {item.slug === "aanbouw" && (
                      <Link
                        to="/aanbouw-kosten"
                        className="inline-flex min-h-11 items-center gap-1 text-foreground hover:text-primary sm:min-h-0 sm:py-1"
                      >
                        Richtprijzen per m²
                        <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>


          <p className="mt-8 text-xs leading-relaxed text-muted-foreground sm:text-sm">
            {kostenDisclaimer}
          </p>
        </div>
      </section>

      <section className="border-t border-border/60 bg-secondary/20 py-14 sm:py-20">
        <div className="container-tight">
          <SectionHeading eyebrow="Veelgestelde vragen" title="Over prijzen en offertes" />

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {kostenFaq.map((item) => (
              <div key={item.vraag} className="rounded-2xl border border-border/60 bg-card p-6">
                <h3 className="font-heading text-base font-semibold text-foreground">
                  {item.vraag}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {item.antwoord}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
